import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BusinessContext = createContext(null);

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

export const BusinessProvider = ({ children, user }) => {
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch businesses for super admin
  const fetchBusinesses = useCallback(async () => {
    if (!isSuperAdmin) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data);
      
      // Auto-select first business if none selected
      if (!selectedBusinessId && response.data.length > 0) {
        const savedBusinessId = localStorage.getItem('selectedBusinessId');
        const businessToSelect = savedBusinessId 
          ? response.data.find(b => b.id === savedBusinessId) || response.data[0]
          : response.data[0];
        setSelectedBusinessId(businessToSelect.id);
        setSelectedBusiness(businessToSelect);
      }
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setLoading(false);
    }
  }, [API, isSuperAdmin, selectedBusinessId]);

  // Fetch single business details for regular users (to get logo_url)
  const fetchUserBusiness = useCallback(async (businessId) => {
    if (!businessId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Use the /my-business endpoint for regular users
      const response = await axios.get(`${API}/my-business`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedBusiness(response.data);
      setSelectedBusinessId(businessId);
    } catch (error) {
      console.error('Failed to fetch user business:', error);
      // Fallback to basic info
      setSelectedBusiness({ id: businessId, name: user?.business_name });
      setSelectedBusinessId(businessId);
    } finally {
      setLoading(false);
    }
  }, [API, user?.business_name]);

  // Load businesses on mount for super admin, or fetch user's business for regular users
  useEffect(() => {
    if (isSuperAdmin) {
      fetchBusinesses();
    } else if (user?.business_id) {
      // For regular users, fetch their assigned business details (including logo)
      fetchUserBusiness(user.business_id);
    }
  }, [isSuperAdmin, user?.business_id, fetchBusinesses, fetchUserBusiness]);

  // Update selected business when ID changes (for super admin switching)
  useEffect(() => {
    if (isSuperAdmin && selectedBusinessId && businesses.length > 0) {
      const business = businesses.find(b => b.id === selectedBusinessId);
      if (business) {
        setSelectedBusiness(business);
        localStorage.setItem('selectedBusinessId', selectedBusinessId);
      }
    }
  }, [selectedBusinessId, businesses, isSuperAdmin]);

  const selectBusiness = useCallback((businessId) => {
    setSelectedBusinessId(businessId);
  }, []);

  const value = {
    businesses,
    selectedBusinessId,
    selectedBusiness,
    selectBusiness,
    isSuperAdmin,
    loading,
    refreshBusinesses: fetchBusinesses,
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};

export default BusinessContext;
